import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertOctagon, AlertTriangle, ArrowRight, X } from "lucide-react";
import api from "../utils/api";

// Banner at the top of every admin page when the chatbot is stopped (no credits) or about to need a recharge.
// The state comes from GET /api/chat-status, which uses the same rule as the chat itself, so it shows
// "stopped" exactly when visitors stop getting answers. Super admins click through to billing (top-up section).

const DISMISS_KEY = "credit_banner_dismissed"; // warnings only; the "stopped" banner cannot be dismissed

// Conversation-billed instances (BILLING_MODE=conversations): the same states, counted in conversations
function conversationBannerContent(status) {
  const limit = status.conversations_limit != null ? Number(status.conversations_limit).toLocaleString() : null;
  const left = status.conversations_left != null ? Number(status.conversations_left).toLocaleString() : null;
  switch (status.state) {
    case "blocked":
      return {
        tone: "red",
        title: "Conversations used up: your chatbot has stopped answering.",
        detail: status.has_plan === false
          ? "Visitors get no replies until you choose a plan."
          : `All ${limit || ""} conversations of this period are used. Add extra conversations or upgrade your plan.`,
        action: status.has_plan === false ? "Choose a plan" : "Add conversations",
      };
    case "low":
      return {
        tone: "amber",
        title: "Conversations running low.",
        detail: left != null && limit != null ? `${left} of ${limit} conversations left this period. The chatbot stops when they run out.` : "The chatbot stops when they run out.",
        action: "Add conversations",
      };
    default:
      return null;
  }
}

function bannerContent(status) {
  if (status.billing_mode === "conversations" && status.state !== "expired") return conversationBannerContent(status);
  const left = status.credits_left != null ? Number(status.credits_left).toLocaleString() : null;
  switch (status.state) {
    case "blocked":
      return {
        tone: "red",
        title: "Credits exhausted: your chatbot has stopped answering.",
        detail: status.has_plan === false ? "Visitors get no replies until you choose a plan." : "Visitors get no replies until credits are added.",
        action: status.has_plan === false ? "Choose a plan" : "Recharge now",
      };
    case "expired":
      return {
        tone: "amber",
        title: "Your subscription has expired.",
        detail: status.next_billing ? `Renewal was due on ${new Date(status.next_billing).toLocaleDateString()}.` : "Renew to avoid an interruption.",
        action: "Renew now",
      };
    case "low":
      return {
        tone: "amber",
        title: "Credits running low.",
        detail: left != null ? `${left} credits left${status.pct_left != null ? ` (${status.pct_left}%)` : ""}. The chatbot stops when they run out.` : "The chatbot stops when they run out.",
        action: "Recharge",
      };
    default:
      return null;
  }
}

const TONES = {
  red: { box: "bg-red-50 border-red-200 text-red-800", icon: "text-red-600", button: "bg-red-600 hover:bg-red-700 text-white" },
  amber: { box: "bg-amber-50 border-amber-200 text-amber-900", icon: "text-amber-600", button: "bg-amber-600 hover:bg-amber-700 text-white" },
};

export default function CreditStatusBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || ""; } catch { return ""; }
  });
  const navigate = useNavigate();
  const location = useLocation();

  const load = useCallback(async () => {
    try {
      const res = await api.request("/api/chat-status");
      if (res && res.success) setStatus(res);
    } catch (_e) {
      // Not logged in yet or a network hiccup: keep the last known state, try again on the next poll
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    window.addEventListener("credits_updated", load);
    window.addEventListener("payment_success", load);
    return () => {
      clearInterval(interval);
      window.removeEventListener("credits_updated", load);
      window.removeEventListener("payment_success", load);
    };
  }, [load]);

  if (!status) return null;
  const content = bannerContent(status);
  if (!content) return null;
  // a dismissed warning comes back when the state changes (e.g. low -> blocked)
  if (status.state !== "blocked" && dismissed === status.state) return null;

  const tone = TONES[content.tone];
  const onBillingPage = location.pathname === "/billing" || location.pathname.startsWith("/billing/");
  const goToBilling = () => navigate(status.has_plan === false ? "/billing" : "/billing?focus=topup");
  const clickable = status.can_recharge && !onBillingPage;

  const dismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, status.state); } catch { /* private mode: dismiss for this page only */ }
    setDismissed(status.state);
  };

  const Icon = content.tone === "red" ? AlertOctagon : AlertTriangle;
  return (
    <div className="px-3 sm:px-6 pt-3" role="alert" data-testid="credit-status-banner" data-state={status.state}>
      <div
        className={`flex items-center gap-3 rounded-xl border px-4 py-2.5 ${tone.box} ${clickable ? "cursor-pointer" : ""}`}
        onClick={clickable ? goToBilling : undefined}
      >
        <Icon size={18} className={`shrink-0 ${tone.icon}`} />
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-semibold">{content.title}</span>{" "}
          <span className="opacity-90">{content.detail}</span>
          {!status.can_recharge && <span className="opacity-90"> Please ask your workspace owner to recharge.</span>}
        </div>
        {clickable && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goToBilling(); }}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${tone.button}`}
          >
            {content.action} <ArrowRight size={14} />
          </button>
        )}
        {status.state !== "blocked" && (
          <button type="button" onClick={dismiss} aria-label="Hide this message" className={`shrink-0 rounded p-1 hover:bg-black/5 ${tone.icon}`}>
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
